import React from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { MapPin, Filter } from 'lucide-react';
import L from 'leaflet';
import { interests } from '../data/interests';

// Custom red marker icon
const redIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const MapView = ({ matchingUsers, handleConnectWithUser, showOnlySharedInterests, setShowOnlySharedInterests, currentUser }) => {
  const switzerlandCenter = [46.8182, 8.2275];
  
  return (
    <div className="relative h-full">
      {/* Filter Toggle */}
      <div className="absolute top-4 left-4 z-[1000]">
        <div className="bg-white rounded-xl shadow-lg p-3">
          <button
            onClick={() => setShowOnlySharedInterests(!showOnlySharedInterests)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              showOnlySharedInterests
                ? 'bg-red-50 text-red-600 border border-red-200'
                : 'bg-gray-50 text-gray-600 border border-gray-200'
            }`}
          >
            <Filter className="w-4 h-4" />
            <span className="text-sm font-medium">
              {showOnlySharedInterests ? 'Shared Interests Only' : 'Show Everyone'}
            </span>
          </button>
          <p className="text-xs text-gray-500 mt-2 px-2">
            {showOnlySharedInterests 
              ? `Showing people with similar interests`
              : `Showing all users near you`
            }
          </p>
        </div>
      </div>
      {/* Leaflet Map */}
      <MapContainer
        center={switzerlandCenter}
        zoom={8}
        style={{ height: '100%', width: '100%' }}
        zoomControl={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {/* User Markers */}
        {matchingUsers.map(user => (
          <Marker
            key={user.id}
            position={[user.coordinates.lat, user.coordinates.lng]}
            icon={redIcon}
          >
            <Popup>
              <div className="p-2">
                <div className="flex items-center space-x-3 mb-3">
                  <img src={user.avatar} alt={user.name} className="w-12 h-12 rounded-full object-cover" />
                  <div>
                    <h3 className="font-semibold text-gray-900">{user.name}</h3>
                    <p className="text-sm text-gray-500 flex items-center">
                      <MapPin className="w-3 h-3 mr-1" />
                      {user.location}
                    </p>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-3">{user.bio}</p>
                <div className="flex flex-wrap gap-1">
                  {user.interests.slice(0, 3).map(interest => {
                    const interestData = interests.find(i => i.id === interest);
                    if (!interestData) return null;
                    const Icon = interestData.icon;
                    return (
                      <span key={interest} className="flex items-center space-x-1 px-2 py-1 bg-red-50 text-red-600 rounded-full text-xs">
                        <Icon className="w-3 h-3" />
                        <span>{interestData.label}</span>
                      </span>
                    );
                  })}
                </div>
                <button 
                  onClick={() => handleConnectWithUser(user)}
                  className="w-full mt-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
                >
                  Connect
                </button>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {/* User Cards Overlay */}
      <div className="absolute bottom-4 left-4 right-4 z-[1000]">
        <div className="flex space-x-4 overflow-x-auto pb-2">
          {matchingUsers.map(user => (
            <div key={user.id} className="flex-shrink-0 w-72 bg-white rounded-2xl shadow-lg p-4 border border-gray-200">
              <div className="flex items-center space-x-3 mb-3">
                <img src={user.avatar} alt={user.name} className="w-12 h-12 rounded-full object-cover" />
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <h3 className="font-semibold text-gray-900">{user.name}</h3>
                    {user.distance !== undefined && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                        {user.distance < 1 ? '<1km' : `${Math.round(user.distance)}km`}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 flex items-center">
                    <MapPin className="w-3 h-3 mr-1" />
                    {user.location}
                  </p>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-3">{user.bio}</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {user.interests.slice(0, 3).map(interest => {
                  const interestData = interests.find(i => i.id === interest);
                  if (!interestData) return null;
                  const Icon = interestData.icon;
                  return (
                    <span key={interest} className="flex items-center space-x-1 px-3 py-1 bg-red-50 text-red-600 rounded-full text-xs">
                      <Icon className="w-3 h-3" />
                      <span>{interestData.label}</span>
                    </span>
                  );
                })}
                {user.interests.length > 3 && (
                  <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                    +{user.interests.length - 3} more
                  </span>
                )}
              </div>
              <button 
                onClick={() => handleConnectWithUser(user)}
                className="w-full py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
              >
                Connect
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MapView;
